import React, { useState } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonLabel,
  IonList,
  IonSegment,
  IonSegmentButton,
  IonBadge,
  IonIcon,
  IonText,
  IonButton,
  IonItem,
} from "@ionic/react";
import { trophyOutline, peopleOutline } from "ionicons/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import api from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { useI18n } from "@/i18n";

interface LeaderboardEntry {
  id: number;
  username: string;
  display_name: string;
  total_points: number;
  league_name: string;
  league_tier: number;
  league_division: string;
  matches_predicted: number;
  exact_scores: number;
}

interface LeagueStanding {
  playerId: number;
  username: string;
  displayName: string;
  totalPoints: number;
  matchesPredicted: number;
  exactScores: number;
  goalDifferences: number;
  tendencies: number;
  position: number;
}

const rankClass = (pos: number) => {
  if (pos === 1) return "rank-badge rank-1";
  if (pos === 2) return "rank-badge rank-2";
  if (pos === 3) return "rank-badge rank-3";
  return "rank-badge rank-default";
};

const zoneClass = (pos: number, total: number) => {
  if (pos <= 3) return "zone-promotion";
  if (pos > total - 3) return "zone-relegation";
  return "";
};

const Leaderboard: React.FC = () => {
  const [viewMode, setViewMode] = useState<"overall" | "my-league">("my-league");
  const { player, updatePlayer } = useAuthStore();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const joinSeason = useMutation(
    async () => (await api.post("/api/leagues/join-season")).data,
    {
      onSuccess: (data) => {
        if (player) {
          updatePlayer({
            ...player,
            currentLeagueId: data.league_id,
            totalPoints: 0,
          });
        }
        queryClient.invalidateQueries(["my-league"]);
        queryClient.invalidateQueries(["seasons-home"]);
        queryClient.invalidateQueries(["leaderboard"]);
      },
      onError: () => {
        // Keep the existing empty-state so the user can retry.
      },
    }
  );

  const { data: overallData, isLoading: overallLoading } = useQuery<{ leaderboard: LeaderboardEntry[] }>(
    ["leaderboard", "overall"],
    async () => (await api.get("/api/leagues/leaderboard?limit=100")).data,
    { enabled: viewMode === "overall" }
  );

  const { data: leagueData, isLoading: leagueLoading } = useQuery<{
    league: any;
    standings: LeagueStanding[];
    player_position: number;
  }>(
    ["my-league"],
    async () => (await api.get("/api/leagues/my-league")).data,
    { enabled: viewMode === "my-league" && !!player }
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("leaderboardTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: "12px 16px" }}>
          <IonSegment value={viewMode} onIonChange={(e) => setViewMode(e.detail.value as any)} style={{ marginBottom: "12px" }}>
            <IonSegmentButton value="my-league"><IonLabel>{t("myLeague")}</IonLabel></IonSegmentButton>
            <IonSegmentButton value="overall"><IonLabel>{t("overall")}</IonLabel></IonSegmentButton>
          </IonSegment>

          {viewMode === "overall" ? (
            <IonCard style={{ margin: 0 }}>
              <IonCardHeader style={{ paddingBottom: "4px" }}>
                <IonCardTitle style={{ fontSize: "16px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <IonIcon icon={trophyOutline} /> {t("overallRankings")}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent style={{ padding: "0" }}>
                {overallLoading ? (
                  <p style={{ padding: "16px" }}>{t("loadingLeaderboard")}</p>
                ) : (
                  <IonList lines="full" style={{ padding: 0 }}>
                    {overallData?.leaderboard.map((entry, index) => {
                      const pos = index + 1;
                      const isCurrent = entry.id === player?.id;
                      return (
                        <IonItem key={entry.id} className={isCurrent ? "player-highlight" : ""} style={{ "--min-height": "52px" }}>
                          <div slot="start" className={rankClass(pos)}>{pos}</div>
                          <IonLabel>
                            <h3 style={{ fontWeight: isCurrent ? 700 : 500, fontSize: "14px" }}>
                              {entry.display_name || entry.username}
                              {isCurrent && <IonText color="secondary"> {t("youTag")}</IonText>}
                            </h3>
                            <p style={{ fontSize: "12px" }}>
                              <IonBadge color="light" style={{ marginRight: "4px", fontSize: "10px" }}>{entry.league_name}</IonBadge>
                              {entry.exact_scores > 0 && <span style={{ color: "var(--ion-color-success)" }}>{entry.exact_scores} {t("exactShort")}</span>}
                            </p>
                          </IonLabel>
                          <div slot="end" className="tabular-nums" style={{ fontWeight: 700, fontSize: "15px" }}>
                            {entry.total_points}
                          </div>
                        </IonItem>
                      );
                    })}
                  </IonList>
                )}
              </IonCardContent>
            </IonCard>
          ) : (
            <>
              {leagueData?.league && (
                <IonCard style={{ margin: "0 0 10px 0" }}>
                  <IonCardContent style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <IonIcon icon={peopleOutline} style={{ fontSize: "24px", color: "var(--ion-color-primary)" }} />
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: "15px" }}>{leagueData.league.name}</strong>
                        <p style={{ margin: "2px 0 0", fontSize: "12px", color: "var(--ion-color-medium)" }}>
                          {t("tierLabel")} {leagueData.league.tier}
                          {leagueData.league.division && ` · ${t("divisionLabel")} ${leagueData.league.division}`}
                          {" · "}{leagueData.league.current_members}/{leagueData.league.max_members} {t("playersLabel")}
                        </p>
                      </div>
                      {leagueData.player_position && (
                        <div className="stat-pill" style={{ minWidth: "auto", padding: "6px 10px" }}>
                          <strong style={{ fontSize: "16px" }}>#{leagueData.player_position}</strong>
                        </div>
                      )}
                    </div>
                  </IonCardContent>
                </IonCard>
              )}

              {leagueLoading ? (
                <p style={{ padding: "16px" }}>{t("loadingLeagueStandings")}</p>
              ) : leagueData?.standings ? (
                <IonCard style={{ margin: 0 }}>
                  <IonCardContent style={{ padding: 0 }}>
                    <IonList lines="full" style={{ padding: 0 }}>
                      {leagueData.standings.map((s) => {
                        const isCurrent = s.playerId === player?.id;
                        const total = leagueData.standings.length;
                        return (
                          <IonItem
                            key={s.playerId}
                            className={`${isCurrent ? "player-highlight" : ""} ${zoneClass(s.position, total)}`}
                            style={{ "--min-height": "52px" }}
                          >
                            <div slot="start" className={rankClass(s.position)}>{s.position}</div>
                            <IonLabel>
                              <h3 style={{ fontWeight: isCurrent ? 700 : 500, fontSize: "14px" }}>
                                {s.displayName || s.username}
                                {isCurrent && <IonText color="secondary"> {t("youTag")}</IonText>}
                              </h3>
                              <p className="egt-text" style={{ color: "var(--ion-color-medium)" }}>
                                <span style={{ color: "var(--app-score-exact)" }}>{s.exactScores}E</span>{" "}
                                <span style={{ color: "var(--app-score-diff)" }}>{s.goalDifferences}G</span>{" "}
                                <span style={{ color: "var(--app-score-tendency)" }}>{s.tendencies}T</span>
                              </p>
                            </IonLabel>
                            <div slot="end" className="tabular-nums" style={{ fontWeight: 700, fontSize: "15px" }}>
                              {s.totalPoints}
                            </div>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  </IonCardContent>
                </IonCard>
              ) : (
                <IonCard>
                  <IonCardContent>
                    <p>{t("notInLeague")}</p>
                    <IonButton
                      expand="block"
                      onClick={() => joinSeason.mutate()}
                      disabled={joinSeason.isLoading}
                      className="save-btn"
                      style={{ marginTop: "12px", "--border-radius": "10px" }}
                    >
                      {joinSeason.isLoading ? t("joiningSeason") : t("joinSeasonButton")}
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              )}

              {/* Zone legend */}
              {leagueData?.league && (
                <div style={{ display: "flex", gap: "8px", marginTop: "12px", fontSize: "11px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "var(--ion-color-success)" }} />
                    {t("promotionZone")} (1-3)
                  </div>
                  {leagueData.league.tier > 1 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "var(--ion-color-danger)" }} />
                      {t("relegationZone")} (16-18)
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Leaderboard;
